﻿import { Directive } from "@app/components/directive";
import { IFormController } from "@app/components/kb-form/kb-form.component";
import { NgComponent } from "@app/di/decorators";
import { Token } from "@app/di/token";
import { Dirs } from "@app/helpers/dirs";
import { KbService } from "@app/services/kb.service";
import { IAugmentedJQuery } from "angular";

export interface IFieldController {
    scope: IFieldScope;
    setFocused: (isFocused: boolean) => void;
    setHasValue: (hasValue: boolean) => void;
    setShouldFloat: (shouldFloat: boolean) => void;
}

interface IFieldScope extends ng.IScope {
    // external members
    required: string;
    label: string;
    desc: string;
    labelLoc: string;
    descLoc: string;
    ngDisabled: boolean;
    // kbField generall gets it's validation messages from the parent kbForm, however
    // it can be overriden by directly supplying validationErrors
    validationErrors: string[];
    warnings: string[];
    runFixThis: Function;
    isCheckbox: boolean;
    helpMedia: string;
    helpUrl: string;
    mobileHelpDropdown: boolean;
    useZeroPadding: boolean;

    // internal directive members
    _checkboxId: string;
    _focused: boolean;
    _hasValue: boolean;
    _runFixThis: Function;
}

/**
 * kbField
 * works in tandem with kbForm.  Will pull the modelType from kbForm and use it to find localized label
 * and description for the field.  It will also search it's direct children looking for an ngModel, as it
 * needs this info to find the model property name being bound to (also used to find the label and description)
 *
 * To override the localized label and description, you can directly set the attributes labelLoc and descLockb
 * to the name of a localization resource.
 *
 * If there is no clear way to find the ngModel child, you can set the modelProp attribute to help it find
 * the label and desc localizations
 *
 * The required attribute can be set to true to have a * show up at the end of the label
 *
 * If the model property is localizeable by the user, set the localize attribute to the model id
 * example:  <kb-field localize="model.id">
 *
 *
 */
@NgComponent({
    token: Token.KbField,
    dependencies: [
        Token.KbService
    ]
})
export class KbField extends Directive {
    constructor(
        kbService: KbService
    ) {
        super();
            
        this.restrict = "EA";
        this.template = Dirs.component("kb-field");
        this.scope = {
            required: "@",
            label: "@",
            labelLoc: "@",
            desc: "@",
            descLoc: "@",
            ngDisabled: "=?",
            validationErrors: "=?",
            runFixThis: "&",
            warnings: "=?",
            fixOptions: "=?",
            isCheckbox: "=?",
            helpMedia: "=?",
            helpUrl: "=?",
            mobileHelpDropdown: "=?",
            useZeroPadding: "=?"
        };
        this.replace = true;
        this.transclude = true;
        this.require = "^?kbForm";

        this.compile = (element, atts: any) => {
            if (atts.mobileHelpDropdown == null) atts.mobileHelpDropdown = "true";
            return this.link;
        };

        this.controller = ["$scope", "$element", function($scope: IFieldScope, $element: IAugmentedJQuery) {
            this.scope = $scope;
            this.setFocused = (focused: boolean) => {
                if ($scope._focused !== focused) {
                    $element.toggleClass("kb-field--is-focused", focused);
                }
                $scope._focused = focused;                
            };
            this.setHasValue = (hasValue: boolean) => {
                if ($scope._hasValue !== hasValue) {
                    $element.toggleClass("kb-field--has-value", hasValue);
                }
                $scope._hasValue = hasValue;                
            };
            this.setShouldFloat = (shouldFloat: boolean) => {
                $element.toggleClass("kb-field--should-float", shouldFloat);
            }
        }];

        this.link = (scope: IFieldScope, element, atts: any, kbForm: IFormController) => {

            if (kbForm) { //integration with kbForm
                let modelProp: string;
                //if in a kbForm, we look in the child input and 
                // scrape the ng - model from it so we know the property we're connecting to
                let $input = element.find(".kb-field__input *[ng-model]").first();
                if ($input.length > 0) {
                    let path = $input.attr("ng-model");
                    modelProp = path.substring(path.lastIndexOf(".") + 1);
                }

                // if title is specified, then it means it's the name of a loc
                let autoLoc: boolean = false; // whether the label/desc is being automatically inferred
                if (modelProp && !scope.labelLoc) {
                    // title was not specified, so we look for a kbForm ancestor that could be providing
                    // model type information
                    
                    if (kbForm && kbForm.scope.modelType && modelProp) {
                        scope.labelLoc = kbForm.scope.modelType + "_" + modelProp + "_title";
                        scope.descLoc = kbForm.scope.modelType + "_" + modelProp + "_desc";
                        autoLoc = true;

                        // check to see if the loc exists... if not, we can try generic resources
                        if (!loc[scope.labelLoc.toLowerCase()]) {
                            scope.labelLoc = "generic_" + modelProp + "_title";
                            scope.descLoc = "generic_" + modelProp + "_desc";
                            // if the generics don't exist either, then we'll just not have a label
                            if (!loc[scope.labelLoc.toLowerCase()]) {
                                scope.labelLoc = "";
                                scope.descLoc = "";
                            }
                        }
                    }
                }

                if (scope.labelLoc) {
                    scope.label = kbService.loc(scope.labelLoc);
                }
                if (scope.descLoc) {
                    let autoDesc = kbService.loc(scope.descLoc);
                    scope.desc = (autoLoc && autoDesc == scope.descLoc) ? "" : autoDesc;
                }

                // validation
                // if the validationErrors attribute was set, then we use it
                // if not set, then we check for a parentForm's validation property that matches our ngmodel expression
                if (!atts.validationErrors) {
                    scope.$watch(() => {
                        if (!kbForm.scope.validation) return null;
                        return kbForm.scope.validation[modelProp];
                    }, newValue => {
                        if (newValue) {
                            scope.validationErrors = [newValue];
                        } else {
                            scope.validationErrors ? scope.validationErrors.clear() : scope.validationErrors = [];
                        }
                    });
                }
            }    
            

            //pass throughs for field controls in a configurator
            scope._runFixThis = (f: any) => {
                return scope.runFixThis({ fix: f });
            };

            // $scope._upload = file => {
            //     return $scope.upload({ file });
            // };

            // $scope._autoCompleteQuery = query => {
            //     return $scope.autoCompleteQuery({ query, field: $scope.field });
            // };
            // $scope._autoCompleteGetLabel = value => {
            //     return $scope.autoCompleteGetLabel({ value, field: $scope.field });
            // };
            // $scope._translateKbo = property => {
            //     return $scope.translateKbo({ kbObject: $scope.field, property });
            // };


            
            
            scope.$on("$destroy", () => {

            });
        };
    }
}
